import { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { getVehicle, updateVehicle, uploadFile, getFileUrl } from '../../lib/supabase'
import { getCommonTrims } from '../../lib/vinDecoder'
import { ArrowLeft, Upload, X, Camera, AlertCircle, CheckCircle } from 'lucide-react'

export default function EditVehicle() {
  const navigate = useNavigate()
  const { vehicleId } = useParams()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const [formData, setFormData] = useState({
    year: '',
    make: '',
    model: '',
    trim: '',
    color: '',
    interior_color: '',
    vin: '',
    license_plate: '',
    mileage: '',
    status: 'stored',
    location: '',
    registration_expiry: '',
    insurance_expiry: '',
    notes: '',
    fuel_type: '',
    engine_info: '',
    drive_type: '',
    condition: ''
  })

  const [existingImages, setExistingImages] = useState([])
  const [newImages, setNewImages] = useState([])
  const [imagePreviews, setImagePreviews] = useState([])

  useEffect(() => {
    loadVehicle()
  }, [vehicleId])

  const loadVehicle = async () => {
    try {
      const { data, error } = await getVehicle(vehicleId)

      if (error) throw error

      // Make sure user owns this vehicle
      if (data.owner_id !== user.id) {
        setError('You do not have permission to edit this vehicle')
        return
      }

      setFormData({
        year: data.year || '',
        make: data.make || '',
        model: data.model || '',
        trim: data.trim || '',
        color: data.color || '',
        interior_color: data.interior_color || '',
        vin: data.vin || '',
        license_plate: data.license_plate || '',
        mileage: data.mileage || '',
        status: data.status || 'stored',
        location: data.location || '',
        registration_expiry: data.registration_expiry || '',
        insurance_expiry: data.insurance_expiry || '',
        notes: data.notes || '',
        fuel_type: data.fuel_type || '',
        engine_info: data.engine_info || '',
        drive_type: data.drive_type || '',
        condition: data.condition || ''
      })

      if (data.images) {
        setExistingImages(data.images)
      }
    } catch (err) {
      console.error('Error loading vehicle:', err)
      setError('Failed to load vehicle')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)
    setNewImages(prev => [...prev, ...files])

    // Create previews
    files.forEach(file => {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index))
    setImagePreviews(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      // Prepare vehicle data
      const vehicleData = {
        year: parseInt(formData.year),
        make: formData.make,
        model: formData.model,
        trim: formData.trim || null,
        color: formData.color || null,
        interior_color: formData.interior_color || null,
        vin: formData.vin || null,
        license_plate: formData.license_plate || null,
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        status: formData.status,
        location: formData.location || null,
        registration_expiry: formData.registration_expiry || null,
        insurance_expiry: formData.insurance_expiry || null,
        notes: formData.notes || null,
        fuel_type: formData.fuel_type || null,
        engine_info: formData.engine_info || null,
        drive_type: formData.drive_type || null,
        condition: formData.condition || null
      }

      // Update vehicle
      const { error: vehicleError } = await updateVehicle(vehicleId, vehicleData)
      if (vehicleError) throw vehicleError

      // Upload new images if any
      for (const image of newImages) {
        const filePath = `${user.id}/${vehicleId}/${Date.now()}-${image.name}`
        const { error: uploadError } = await uploadFile('vehicle-images', filePath, image)
        if (uploadError) {
          console.error('Image upload error:', uploadError)
        } else if (!vehicleData.primary_image_url) {
          // Set as primary image if vehicle doesn't have one
          const url = getFileUrl('vehicle-images', filePath)
          await updateVehicle(vehicleId, { primary_image_url: url })
        }
      }

      setSuccess(true)
      setTimeout(() => {
        navigate(`/garage/${vehicleId}`)
      }, 1500)
    } catch (err) {
      console.error('Error updating vehicle:', err)
      setError(err.message || 'Failed to update vehicle')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-primary border-t-transparent" />
      </div>
    )
  }

  if (error && !formData.make) {
    return (
      <div className="animate-fade-in max-w-2xl">
        <div className="mb-6">
          <Link to="/garage" className="inline-flex items-center text-rdc-taupe hover:text-black">
            <ArrowLeft size={20} className="mr-2" />
            Back to Garage
          </Link>
        </div>
        <div className="card p-8 text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="font-display text-xl font-semibold text-black mb-2">Error</h2>
          <p className="text-rdc-taupe">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="mb-6">
        <Link to={`/garage/${vehicleId}`} className="inline-flex items-center text-rdc-taupe hover:text-black">
          <ArrowLeft size={20} className="mr-2" />
          Back to Vehicle
        </Link>
      </div>

      <div className="mb-8">
        <h1 className="page-title">Edit Vehicle</h1>
        <p className="text-rdc-taupe mt-1">
          Update your {formData.year} {formData.make} {formData.model}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
          <CheckCircle size={20} />
          <span>Vehicle updated successfully! Redirecting...</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card p-6">
          <h2 className="section-title mb-4">Basic Information</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Year *
              </label>
              <input
                type="number"
                name="year"
                value={formData.year}
                onChange={handleChange}
                className="input-field"
                min="1900"
                max={new Date().getFullYear() + 1}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Make *
              </label>
              <input
                type="text"
                name="make"
                value={formData.make}
                onChange={handleChange}
                className="input-field"
                placeholder="Ferrari"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Model *
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="input-field"
                placeholder="488 GTB"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Trim
              </label>
              <input
                type="text"
                name="trim"
                value={formData.trim}
                onChange={handleChange}
                className="input-field"
                placeholder="e.g. GT3, SS, Shelby GT350"
                list="trim-suggestions"
              />
              <datalist id="trim-suggestions">
                {getCommonTrims(formData.make).map(t => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Exterior Color
              </label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="input-field"
                placeholder="Rosso Corsa"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Interior Color
              </label>
              <input
                type="text"
                name="interior_color"
                value={formData.interior_color}
                onChange={handleChange}
                className="input-field"
                placeholder="Nero Leather"
              />
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="card p-6">
          <h2 className="section-title mb-4">Vehicle Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                VIN
              </label>
              <input
                type="text"
                name="vin"
                value={formData.vin}
                onChange={handleChange}
                className="input-field font-mono"
                placeholder="WVWZZZ3CZWE123456"
                maxLength={17}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                License Plate
              </label>
              <input
                type="text"
                name="license_plate"
                value={formData.license_plate}
                onChange={handleChange}
                className="input-field"
                placeholder="ABC1234"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Powertrain / Fuel Type
              </label>
              <select
                name="fuel_type"
                value={formData.fuel_type}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Not specified</option>
                <option value="Gasoline">Gasoline</option>
                <option value="Diesel">Diesel</option>
                <option value="Electric">Electric</option>
                <option value="Plug-In Hybrid">Plug-In Hybrid</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Hydrogen">Hydrogen</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Current Mileage
              </label>
              <input
                type="number"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                className="input-field"
                placeholder="12500"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-field"
              >
                <option value="stored">Stored</option>
                <option value="in_service">In Service</option>
                <option value="in_transit">In Transit</option>
                <option value="with_owner">With Owner</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
              Storage Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="input-field"
              placeholder="Main Facility - Bay 12"
            />
          </div>
        </div>

        {/* Vehicle Condition */}
        <div className="card p-6">
          <h2 className="section-title mb-4">Vehicle Condition</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                value: 'concours',
                title: 'Concours / Show Quality',
                description: 'Museum-quality restoration or flawless original. Paint, interior, engine bay, and undercarriage are essentially perfect.'
              },
              {
                value: 'excellent',
                title: 'Excellent',
                description: 'Very well maintained with minimal wear. Could be shown at local events. No significant cosmetic or mechanical issues.'
              },
              {
                value: 'good',
                title: 'Good',
                description: 'Presentable and mechanically sound. Normal wear for age and mileage. May have minor cosmetic imperfections.'
              },
              {
                value: 'fair',
                title: 'Fair',
                description: 'Runs and drives but shows significant wear. May need cosmetic work or minor mechanical attention. Suitable for a driver.'
              },
              {
                value: 'project',
                title: 'Project / Restoration',
                description: 'Needs significant mechanical or cosmetic work. May not be currently drivable. Restoration candidate.'
              }
            ].map(option => (
              <label
                key={option.value}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.condition === option.value
                    ? 'border-rdc-primary bg-blue-50'
                    : 'border-rdc-warm-gray hover:border-rdc-primary'
                }`}
              >
                <div className="flex items-start">
                  <input
                    type="radio"
                    name="condition"
                    value={option.value}
                    checked={formData.condition === option.value}
                    onChange={handleChange}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-rdc-dark-gray">{option.title}</p>
                    <p className="text-sm text-rdc-taupe mt-1">{option.description}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div className="card p-6">
          <h2 className="section-title mb-4">Important Dates</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Registration Expires
              </label>
              <input
                type="date"
                name="registration_expiry"
                value={formData.registration_expiry}
                onChange={handleChange}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Insurance Expires
              </label>
              <input
                type="date"
                name="insurance_expiry"
                value={formData.insurance_expiry}
                onChange={handleChange}
                className="input-field"
              />
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="card p-6">
          <h2 className="section-title mb-4">Photos</h2>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-rdc-taupe mb-2">Current photos:</p>
              <div className="grid grid-cols-3 gap-4">
                {existingImages.map((image) => (
                  <div key={image.id} className="relative aspect-video bg-rdc-cream rounded-lg overflow-hidden">
                    <img
                      src={image.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Image Upload */}
          <div className="border-2 border-dashed border-rdc-warm-gray rounded-lg p-6 text-center">
            <Camera size={32} className="text-rdc-taupe mx-auto mb-2" />
            <p className="text-sm text-rdc-taupe mb-2">Add more photos</p>
            <label className="btn-secondary cursor-pointer inline-block">
              <Upload size={16} className="inline mr-2" />
              Choose Files
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>

          {/* New Image Previews */}
          {imagePreviews.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mt-4">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative aspect-video bg-rdc-cream rounded-lg overflow-hidden">
                  <img src={preview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeNewImage(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="card p-6">
          <h2 className="section-title mb-4">Notes</h2>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="input-field min-h-[100px]"
            placeholder="Any special notes about this vehicle..."
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Link to={`/garage/${vehicleId}`} className="btn-secondary">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  )
}
